import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, useInView } from 'framer-motion';

// ==========================================
// FLOATING PARTICLES
// ==========================================
export function Particles({ count = 20 }) {
  const particles = useMemo(() => Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    delay: `${Math.random() * 8}s`,
    duration: `${6 + Math.random() * 10}s`,
    size: Math.random() > 0.7 ? 4 : 2,
    color: Math.random() > 0.5 ? 'rgba(245,158,11,0.4)' : 'rgba(30,58,138,0.4)',
  })), [count]);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: p.left, top: p.top,
            width: p.size, height: p.size,
            background: p.color,
            animationDelay: p.delay,
            animationDuration: p.duration,
          }}
        />
      ))}
    </div>
  );
}

// ==========================================
// MOUSE REACTIVE GRADIENT
// ==========================================
export function MouseGradient() {
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current?.parentElement;
    if (!el) return;
    const handler = (e) => {
      const rect = el.getBoundingClientRect();
      setPos({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100,
      });
    };
    el.addEventListener('mousemove', handler);
    return () => el.removeEventListener('mousemove', handler);
  }, []);

  return (
    <div
      ref={ref}
      className="absolute inset-0 pointer-events-none transition-opacity duration-1000"
      style={{
        background: `radial-gradient(600px circle at ${pos.x}% ${pos.y}%, rgba(245,158,11,0.06), transparent 60%)`,
      }}
    />
  );
}

// ==========================================
// SCROLL REVEAL WRAPPER
// ==========================================
export function Reveal({ children, delay = 0, direction = 'up' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const y = direction === 'up' ? 30 : direction === 'down' ? -30 : 0;
  const x = direction === 'left' ? 30 : direction === 'right' ? -30 : 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y, x }}
      animate={inView ? { opacity: 1, y: 0, x: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ==========================================
// ANIMATED COUNTER
// ==========================================
export function AnimatedCounter({ target, suffix = '', duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const increment = target / (duration * 60);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

// ==========================================
// DARK SECTION WRAPPER (grid + glow + particles)
// ==========================================
export function DarkSection({ children, className = '', withParticles = true, withGrid = true, id }) {
  return (
    <section className={`relative overflow-hidden bg-[#0F172A] ${className}`} data-testid={id}>
      {withGrid && (
        <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '60px 60px'}} />
      )}
      <div className="hero-grid-glow" />
      {withParticles && <Particles count={15} />}
      <MouseGradient />
      <div className="container-custom relative z-10">
        {children}
      </div>
    </section>
  );
}
